import { Edit, PackagePlus, Trash2 } from "lucide-react";
import type { TFunction } from "i18next";
import { Badge } from "../../../components/badge";
import { Button } from "../../../components/button";
import { AppTooltip, Tooltip, TooltipContent, TooltipTrigger } from "../../../components/tooltip";
import type { DataTableColumn } from "../../../components/table";
import type { DataTableNumberRangeValue } from "../../../components/table";
import timeHelper from "../../../lib/date-time/time";
import { EGiftCardStatus } from "../types/eGiftCard.types";
import type {
  EGiftCardFilter,
  EGiftCardListItemResponse,
  EGiftCardNumberRange,
} from "../types/eGiftCard.types";
import type { PublicOptionResponse } from "../../common";

type BuildEGiftCardListColumnsParams = {
  t: TFunction;
  filters: EGiftCardFilter;
  updateFilter: (key: keyof EGiftCardFilter, value: EGiftCardFilter[keyof EGiftCardFilter]) => void;
  onRestock?: (eGiftCard: EGiftCardListItemResponse) => void;
  onEdit?: (eGiftCard: EGiftCardListItemResponse) => void;
  onDelete?: (eGiftCard: EGiftCardListItemResponse) => void;
  merchantBrandOptions?: PublicOptionResponse[];
};
const emptyRange = (range?: EGiftCardNumberRange | null) => range ?? { min: null, max: null };

const normalizeRange = (range: DataTableNumberRangeValue): EGiftCardNumberRange | null =>
  range.min === null && range.max === null ? null : range;

const formatDate = (value?: string) => (value ? timeHelper.formatTime(value, "MM/DD/YYYY") : "-");

export const getEGiftCardStatus = (eGiftCard: EGiftCardListItemResponse) => {
  if (eGiftCard.status === EGiftCardStatus.Expired) {
    return { label: "Expired", className: "bg-secondary text-secondary-foreground" };
  }
  if (eGiftCard.is_alert_low_stock && eGiftCard.status === EGiftCardStatus.LowStock) {
    return { label: "Low Stock", className: "bg-amber-50 text-amber-700" };
  }
  if (eGiftCard.is_alert_low_stock && eGiftCard.status === EGiftCardStatus.OutOfStock) {
    return { label: "Out of Stock", className: "bg-red-50 text-red-600" };
  }
  if (
    eGiftCard.status === EGiftCardStatus.LowStock ||
    eGiftCard.status === EGiftCardStatus.OutOfStock
  ) {
    return {
      label: "Active",
      className: "bg-[#E2FBE8] text-[#016630] dark:bg-emerald-900/30 dark:text-emerald-300",
    };
  }
  if (eGiftCard.status !== EGiftCardStatus.Active) {
    return { label: eGiftCard.status || "Inactive", className: "bg-gray-200 text-gray-700" };
  }
  return {
    label: "Active",
    className: "bg-[#E2FBE8] text-[#016630] dark:bg-emerald-900/30 dark:text-emerald-300",
  };
};

export const needsEGiftCardRestock = (availableQuantity: number): boolean => (
  Number.isFinite(availableQuantity) && availableQuantity >= 0 && availableQuantity <= 1
);

const ActionButton = ({
  disabled,
  label,
  children,
  onClick,
}: {
  disabled: boolean;
  label: string;
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 w-8 rounded-md p-0"
          disabled={disabled}
          aria-label={label}
          onClick={onClick}
        >
          {children}
        </Button>
      </span>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

export const buildEGiftCardListColumns = ({
  t,
  filters,
  updateFilter,
  onRestock,
  onEdit,
  onDelete,
  merchantBrandOptions,
}: BuildEGiftCardListColumnsParams): DataTableColumn<EGiftCardListItemResponse>[] => [
  {
    key: "image",
    title: t("common.image"),
    render: (eGiftCard) => (
      <div className="h-14 w-14 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        {eGiftCard.image_url && (
          <img
            className="h-full w-full object-cover"
            src={eGiftCard.image_url}
            alt={eGiftCard.title || eGiftCard.merchant_name}
          />
        )}
      </div>
    ),
    cellClassName: "w-20",
  },
  {
    key: "merchant_name",
    title: t("common.merchant"),
    filter: {
      type: "dropdown",
      value: filters.merchant_id || "",
      onFilter: (value: string) => updateFilter("merchant_id", value || null),
      options: merchantBrandOptions || [],
      placeholder: t("eGiftCard.message.search_merchant_name"),
      title: t("eGiftCard.message.filter_by_merchant_name"),
      clearLabel: t("common.clear"),
    },
    render: (eGiftCard) => (
      <div>
        <div>{eGiftCard.merchant_name || "-"}</div>
      </div>
    ),
  },
  {
    key: "title",
    title: t("eGiftCard.name"),
    render: (eGiftCard) => (
      <span className="block max-w-56 truncate" title={eGiftCard.title}>
        {eGiftCard.title || "-"}
      </span>
    ),
  },
  {
    key: "points_required",
    title: t("eGiftCard.value"),
    filter: {
      type: "range_number",
      value: emptyRange(filters.points_required),
      onFilter: (value: DataTableNumberRangeValue) =>
        updateFilter("points_required", normalizeRange(value)),
      title: t("eGiftCard.message.filter_by_value"),
      minLabel: "From",
      maxLabel: "To",
    },
    render: (eGiftCard) => (
      <span>
        ${eGiftCard.points_required / 100} ({eGiftCard.points_required} pts)
      </span>
    ),
  },
  {
    key: "active_period",
    title: t("eGiftCard.active_period"),
    render: (eGiftCard) => (
      <div className="text-sm">
        <div>{`${formatDate(eGiftCard.valid_from)} - ${formatDate(eGiftCard.valid_to)}`}</div>
      </div>
    ),
  },
  ...(["quantity", "used_quantity", "available_quantity"] as const).map((key) => ({
    key,
    title: t(`eGiftCard.${key}`),
    filter: {
      type: "range_number" as const,
      value: emptyRange(filters[key]),
      onFilter: (value: DataTableNumberRangeValue) => updateFilter(key, normalizeRange(value)),
      title: t(
        `eGiftCard.message.${key === "quantity" ? "filter_by_quantity" : key === "used_quantity" ? "filter_by_exchanged" : "filter_by_remaining"}`,
      ),
    },
    render: (eGiftCard: EGiftCardListItemResponse) => {
      if (key === "quantity" && eGiftCard.quantity === -1) return t("eGiftCard.unlimited");
      const value = eGiftCard[key] || 0;
      if (key !== "available_quantity") return <span>{value}</span>;
      return (
        <div className="flex flex-col items-center gap-1">
          <span>{value}</span>
          {eGiftCard.is_alert_low_stock && value < 2 && (
            <Badge
              className={
                value < 1
                  ? "border-0 bg-red-50 text-red-600"
                  : "border-0 bg-amber-50 text-amber-700"
              }
            >
              {value < 1 ? "Out of Stock" : "Low Stock"}
            </Badge>
          )}
        </div>
      );
    },
    align: "center" as const,
  })),
  {
    key: "status",
    title: t("eGiftCard.status"),
    filter: {
      type: "dropdown",
      value: filters.requested_status || "",
      onFilter: (value: string) =>
        updateFilter("requested_status", value ? (value as EGiftCardStatus) : null),
      title: t("eGiftCard.message.filter_by_status"),
      placeholder: t("eGiftCard.all_status"),
      clearLabel: t("common.clear"),
      options: [
        { value: EGiftCardStatus.Active, label: "Active" },
        { value: EGiftCardStatus.LowStock, label: "Low Stock" },
        { value: EGiftCardStatus.OutOfStock, label: "Out of Stock" },
        { value: EGiftCardStatus.Expired, label: "Expired" },
      ],
    },
    render: (eGiftCard) => {
      const status = getEGiftCardStatus(eGiftCard);
      return <Badge className={`border-0 ${status.className}`}>{status.label}</Badge>;
    },
    align: "center",
  },
  {
    key: "actions",
    title: t("common.actions"),
    render: (eGiftCard) => (
      <div
        className="flex items-center justify-end gap-2"
        onClick={(event) => event.stopPropagation()}
      >
        {needsEGiftCardRestock(eGiftCard.available_quantity) && onRestock && (
          <AppTooltip
            title={`Add inventory — ${eGiftCard.available_quantity === 0 ? "out of stock" : "1 remaining"}`}
          >
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="h-9 shrink-0 rounded-md px-3 text-xs shadow-sm"
              aria-label={`Restock ${eGiftCard.title}`}
              onClick={() => onRestock(eGiftCard)}
            >
              <PackagePlus aria-hidden="true" />
              Restock
            </Button>
          </AppTooltip>
        )}
        <ActionButton
          disabled={false}
          label={t("common.edit")}
          onClick={(event) => {
            event.stopPropagation();
            onEdit?.(eGiftCard);
          }}
        >
          <Edit className="h-4 w-4" />
        </ActionButton>
        <ActionButton
          disabled={false}
          label={t("common.delete")}
          onClick={(event) => {
            event.stopPropagation();
            onDelete?.(eGiftCard);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-600" />
        </ActionButton>
      </div>
    ),
    align: "right",
  },
];
